import SaveIcon from '@mui/icons-material/Save'
import RestoreIcon from '@mui/icons-material/Restore'
import UndoIcon from '@mui/icons-material/Undo'
import RedoIcon from '@mui/icons-material/Redo'
import DownloadIcon from '@mui/icons-material/Download'
import SearchIcon from '@mui/icons-material/Search'
import PublishIcon from '@mui/icons-material/Publish'
import DeleteIcon from '@mui/icons-material/Delete'
import EditNoteIcon from '@mui/icons-material/EditNote'
import TableRowsIcon from '@mui/icons-material/TableRows'
import {
  ColumnsPanelTrigger,
  gridFilteredSortedRowIdsSelector,
  GridRowSelectionModel,
  ToolbarButton,
  Toolbar,
  FilterPanelTrigger,
  QuickFilter,
  QuickFilterControl,
  QuickFilterClear,
} from '@mui/x-data-grid-premium'
import {
  MutableRefObject,
  ReactNode,
  useState,
  useCallback,
  useMemo,
} from 'react'
import { downloadAsCsv } from 'Common/utils/utils'
import { AcceptedData } from 'export-to-csv/output/lib/types'
// SimpleUpload removed - using permanent file input approach
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'
import {
  Badge,
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded'
import FilterListIcon from '@mui/icons-material/FilterList'
import CancelIcon from '@mui/icons-material/Cancel'
import {
  FONT_SANS,
  PillButton,
  RADII,
  SchChip,
  TOKENS,
} from 'Features/Scheduler/SchedulerDesign'

const excludedColumns = new Set(['id', 'actions', '__check__'])
const excelMayThrowIssueColumns = new Set(['batchGroupingCode', 'skuCode'])

export type MUIToolbarComponentProps = {
  hasUnsavedRows: boolean
  /** How many rows are edited but not yet written. Labels the Save
   *  button, so a planner commits a known quantity rather than a verb. */
  unsavedCount?: number
  isSaving: boolean
  saveChanges: () => void
  discardChanges: () => void
  dataToDownload?: Record<string, AcceptedData>[]
  apiRef?: MutableRefObject<GridApiPremium> | null
  selectedRows: GridRowSelectionModel
  onBulkUpdateButtonClick?: () => void
  onDeleteRowsButtonClick?: () => void
  isDeleting?: boolean
  uploadData?: (file: File) => void
  isUploading?: boolean
  isLoading?: boolean
  onUndo?: () => void
  onRedo?: () => void
  canUndo?: boolean
  canRedo?: boolean
  customDownloadDataMethod?: (
    tableData: Record<string, AcceptedData>[]
  ) => Record<string, AcceptedData>[]
  expandDownloadRow?: (
    out: Record<string, AcceptedData>,
    row: Record<string, unknown>
  ) => Record<string, AcceptedData>
  /** Shows "Save as my default" and "Reset to standard view" when the table
   *  saves its layout (`BaseTable`'s `layoutKey`). */
  onSaveLayout?: () => void
  onResetLayout?: () => void
  /** Fields written to the CSV download even while their column is hidden —
   *  `BaseTable` passes every column an upload accepts. */
  alwaysDownloadFields?: string[]
}

/**
 * Toolbar primitives, transcribed from the design handoff rather than
 * invented: `design_handoff_rules_and_constraints/scheduler/preview/
 * comp-buttons.html` for the button matrix, and `ui_kit_v3/
 * InputTablesView.jsx` for this header's own composition.
 *
 * They are local on purpose. `SchedulerDesign`'s `PillButton` predates the
 * handoff and diverges from it in several ways — no fixed heights, its
 * `outline` uses ink-700 where the matrix's `ghost` uses teal, there is no
 * `secondary` or `danger`, disabled is .5 against the matrix's .4, and there
 * is no focus ring. Reconciling it is worth doing, but it restyles every
 * button in the app, so it does not belong inside a toolbar change.
 */

/** 28x28 bordered icon button — `iconBtn` in InputTablesView.jsx. */
const IconBtn = ({
  title,
  onClick,
  disabled,
  danger,
  children,
}: {
  title: string
  onClick?: () => void
  disabled?: boolean
  danger?: boolean
  children: ReactNode
}) => (
  <Tooltip title={title} enterDelay={400}>
    {/* A disabled button fires no events, so Tooltip needs a real element to
        listen on — without this wrapper MUI warns and the tip never shows,
        which is exactly when the reason for the disabling matters most. */}
    <span style={{ display: 'inline-flex' }}>
      <Box
        component="button"
        type="button"
        aria-label={title}
        onClick={onClick}
        disabled={disabled}
        sx={{
          width: 28,
          height: 28,
          flexShrink: 0,
          border: `1px solid ${TOKENS.line300}`,
          borderRadius: RADII.sm,
          background: TOKENS.surface0,
          // Danger colour only while it can actually fire. A disabled red
          // trash can is the loudest thing in the row and does nothing,
          // which is the wrong way round.
          color: danger && !disabled ? TOKENS.stateFailed : TOKENS.ink500,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          transition: 'all .12s',
          '& svg': { fontSize: 16 },
          '&:hover': {
            background: danger ? '#FCF0EE' : TOKENS.tealTint,
            borderColor: danger ? TOKENS.stateFailed : TOKENS.teal300,
          },
          '&:focus-visible': {
            outline: 'none',
            boxShadow: '0 0 0 3px rgba(0,130,179,.28)',
          },
          '&:disabled': {
            opacity: 0.4,
            cursor: 'not-allowed',
            background: TOKENS.surface0,
            borderColor: TOKENS.line300,
          },
        }}
      >
        {children}
      </Box>
    </span>
  </Tooltip>
)

/** Thin gap between action groups — the handoff separates by space, not rules. */
/** Separator between action groups.
 *
 *  Sized against the 4px sibling gap, not in isolation: grouping only reads
 *  if the gap between groups is several times the gap within one. At 8px
 *  within and 14px between, the row rendered as nine evenly spaced squares
 *  and the grouping did nothing. */
const GroupGap = () => <Box sx={{ width: '14px', flexShrink: 0 }} />

export const MUITableToolBarComponent = ({
  hasUnsavedRows,
  unsavedCount = 0,
  isSaving,
  saveChanges,
  discardChanges,
  dataToDownload,
  fileName,
  apiRef = null,
  selectedRows,
  onBulkUpdateButtonClick,
  onDeleteRowsButtonClick,
  isDeleting = false,
  uploadData,
  isUploading = false,
  customDownloadDataMethod = undefined,
  expandDownloadRow = undefined,
  onSaveLayout,
  onResetLayout,
  alwaysDownloadFields,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  children,
}: MUIToolbarComponentProps & {
  fileName: string
  children?: ReactNode
}) => {
  // The handoff's header leads with a row count. `dataToDownload` is the
  // table's own rows (BaseTable passes `config.data`), so this needs no new
  // prop and no apiRef read.
  const rowCount = dataToDownload?.length ?? 0

  const hasSelection =
    selectedRows &&
    selectedRows.ids &&
    ((selectedRows.type === 'include' && selectedRows.ids.size > 0) ||
      selectedRows.type === 'exclude')

  const filterPanelRender = useCallback(
    (props: any, state: any) => (
      <ToolbarButton {...props} color="default">
        <Badge badgeContent={state.filterCount} color="primary" variant="dot">
          <FilterListIcon fontSize="small" />
        </Badge>
      </ToolbarButton>
    ),
    []
  )

  const columnsPanelRender = useMemo(() => <ToolbarButton />, [])

  const quickFilterControlRender = useCallback(
    (
      { ref, ...controlProps }: any,
      state: { expanded: boolean; value: string }
    ) => (
      <TextField
        {...controlProps}
        inputRef={ref}
        aria-label="Search"
        placeholder="Filter…"
        size="small"
        // 200x28 bordered box with a 12px borderless input, matching
        // InputTablesView.jsx. Fixed width, always visible: the grid's own
        // expanding-magnifier pattern hides the one control people reach for
        // first on a 350-row table.
        sx={{
          width: 200,
          flexShrink: 0,
          // `borderRadius` on the root is not enough: the notched outline
          // draws its own corners and keeps MUI's 4px default, which is why
          // this box looked rounder than the 2px squares next to it.
          '& .MuiOutlinedInput-notchedOutline': { borderRadius: RADII.sm },
          '& .MuiOutlinedInput-root': {
            height: 28,
            borderRadius: RADII.sm,
            background: TOKENS.surface0,
            fontFamily: FONT_SANS,
            fontSize: 12,
            '& fieldset': { borderColor: TOKENS.line300 },
            '&:hover fieldset': { borderColor: TOKENS.teal300 },
            '&.Mui-focused fieldset': {
              borderColor: TOKENS.brandBlue,
              borderWidth: 1,
            },
          },
          '& .MuiOutlinedInput-input': { padding: '0 4px' },
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: state.value ? (
              <InputAdornment position="end">
                <QuickFilterClear
                  edge="end"
                  size="small"
                  aria-label="Clear search"
                  material={{ sx: { marginRight: -0.75 } }}
                >
                  <CancelIcon fontSize="small" />
                </QuickFilterClear>
              </InputAdornment>
            ) : null,
            ...controlProps.slotProps?.input,
          },
          ...controlProps.slotProps,
        }}
      />
    ),
    []
  )

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${TOKENS.line200}`,
        backgroundColor: TOKENS.surface0,
      }}
    >
      <Toolbar>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            // Tight within a group; `GroupGap` opens it up between them.
            gap: '4px',
            width: '100%',
            minHeight: 40,
            px: '4px',
            flexWrap: 'wrap',
          }}
        >
          {/* Identity, left. Straight from InputTablesView.jsx: the table
              icon, then what you are looking at and how much of it. The
              section name itself is already rendered by the page above the
              grid, so this carries the row count rather than repeating it. */}
          <TableRowsIcon sx={{ fontSize: 16, color: TOKENS.brandTeal }} />
          {/* The handoff calls this chip `outline`; SchChip's equivalent
              tone is `neutral` — surface-50 on a line-300 border, same
              thing under a different name. */}
          <SchChip tone="neutral" mono>
            {rowCount} rows
          </SchChip>

          <Box sx={{ flex: 1, minWidth: '8px' }} />

          {/* Search, always visible at 200x28 — the handoff shows a standing
              filter box, not a magnifier that expands into one. The grid's
              QuickFilter still drives it, so debouncing and clearing are
              unchanged; only the chrome is ours. */}
          <QuickFilter debounceMs={300} expanded>
            <QuickFilterControl render={quickFilterControlRender} />
          </QuickFilter>

          <GroupGap />

          {/* One word, one icon, always the same ones, so the button's
              shape never moves as the grid goes dirty and clean again. How
              much is pending belongs in the tooltip, not in a label that
              would resize the button on every keystroke.
              
              Only the COLOUR carries state: bordered white like every other
              control here until there is something to commit, teal the
              moment there is. A Save that is teal all the time spends the
              header's one piece of emphasis on a button that usually has
              nothing to do. */}
          <PillButton
            kind={hasUnsavedRows || isSaving ? 'primary' : 'ghost'}
            size="sm"
            disabled={!hasUnsavedRows || isSaving}
            onClick={saveChanges}
            title={
              isSaving
                ? 'Saving…'
                : hasUnsavedRows
                  ? `Save ${unsavedCount} edited row${unsavedCount === 1 ? '' : 's'}`
                  : 'No unsaved changes'
            }
            // Height 28 to sit on the header's row, which InputTablesView.jsx
            // sets with its 28px icon buttons; the button matrix has 24 and 32
            // and no 28, so the two halves of the handoff disagree and the row
            // it lives in wins.
            //
            // `px`, NOT the `padding` shorthand. PillButton's base sx sets
            // `padding` from the size chart, and MUI's sx merge did not let a
            // `padding` string in the caller's sx replace it — three rounds of
            // raising that number changed nothing on screen while `tsc` and
            // Prettier passed against it. `px` is a different key, so it
            // survives the merge and emits padding-left/right after the
            // shorthand. The Run scheduler button in SchedulerInput.topStrip
            // has always done it this way, which is why that one looks right.
            //
            // 16px rather than the size chart's 9px because this is the only
            // labelled control in a row of nine square icons; the width is
            // what stops it reading as a tenth icon with a word in it.
            sx={{
              height: 28,
              // Three ways of saying the same thing, on purpose.
              //
              // `padding` alone did not work: PillButton's base sx already
              // sets `padding` from the size chart and the caller's value was
              // not replacing it, so three rounds of raising the number moved
              // nothing on screen. `px` is what the Run scheduler button uses
              // and that one renders correctly, so it is here too — but
              // `minWidth` is the one that cannot fail, because it depends on
              // no merge order at all: the button is at least this wide and
              // `justifyContent: center` puts the space on both sides.
              //
              // If you are tidying this later, check the rendered padding in
              // the inspector before removing any of the three.
              px: '11px',
              paddingLeft: '11px',
              paddingRight: '11px',
              minWidth: 78,
              '& svg': { fontSize: 15 },
            }}
            icon={
              isSaving ? (
                <CircularProgress size={13} sx={{ color: 'inherit' }} />
              ) : (
                <SaveIcon />
              )
            }
          >
            {isSaving ? 'Saving…' : 'Save'}
          </PillButton>

          {/* Edit group. */}
          <IconBtn
            title="Discard changes"
            disabled={!hasUnsavedRows || isSaving}
            onClick={discardChanges}
          >
            <RestoreIcon />
          </IconBtn>
          {onUndo && (
            <IconBtn title="Undo (Ctrl+Z)" disabled={!canUndo} onClick={onUndo}>
              <UndoIcon />
            </IconBtn>
          )}
          {onRedo && (
            <IconBtn title="Redo (Ctrl+Y)" disabled={!canRedo} onClick={onRedo}>
              <RedoIcon />
            </IconBtn>
          )}

          <GroupGap />

          {/* Selection group. The tooltip says why it is dead when nothing
              is ticked — a disabled icon with no label and no explanation is
              unreadable. */}
          <IconBtn
            title={
              hasSelection
                ? 'Bulk edit selected rows'
                : 'Select rows to bulk edit'
            }
            disabled={!hasSelection || isSaving}
            onClick={onBulkUpdateButtonClick}
          >
            <EditNoteIcon />
          </IconBtn>
          {onDeleteRowsButtonClick && (
            <IconBtn
              danger
              title={
                hasSelection ? 'Delete selected rows' : 'Select rows to delete'
              }
              disabled={!hasSelection || isSaving || isDeleting}
              onClick={onDeleteRowsButtonClick}
            >
              {isDeleting ? (
                <CircularProgress size={13} sx={{ color: 'inherit' }} />
              ) : (
                <DeleteIcon />
              )}
            </IconBtn>
          )}

          <GroupGap />

          {/* Data in and out. */}
          {uploadData && (
            <IconBtn
              title="Upload CSV"
              disabled={isUploading}
              onClick={() => {
                const fileInput = document.getElementById(
                  'base-table-file-upload'
                ) as HTMLInputElement
                if (fileInput) {
                  fileInput.click()
                } else {
                  console.error(
                    'MUITableToolBar: File input #base-table-file-upload not found!'
                  )
                }
              }}
            >
              {isUploading ? (
                <CircularProgress size={13} sx={{ color: 'inherit' }} />
              ) : (
                <PublishIcon />
              )}
            </IconBtn>
          )}
          <ToolBarDownloadCSVButton
            fileName={fileName}
            data={dataToDownload ?? []}
            apiRef={apiRef}
            customDownloadDataMethod={customDownloadDataMethod}
            expandDownloadRow={expandDownloadRow}
            alwaysDownloadFields={alwaysDownloadFields}
          />

          <GroupGap />

          {/* View group — changes what you see, never the data. */}
          <Tooltip title="Columns" enterDelay={400}>
            <ColumnsPanelTrigger render={columnsPanelRender}>
              <ViewColumnIcon fontSize="small" />
            </ColumnsPanelTrigger>
          </Tooltip>
          <Tooltip title="Filters" enterDelay={400}>
            <FilterPanelTrigger render={filterPanelRender} />
          </Tooltip>
          {onSaveLayout && (
            <IconBtn
              title="Save as my default — columns, order, widths, pinning and sort open this way on every schedule"
              onClick={onSaveLayout}
            >
              <BookmarkAddedIcon />
            </IconBtn>
          )}
          {onResetLayout && (
            <IconBtn
              title="Reset to standard view — clears your saved default"
              onClick={onResetLayout}
            >
              <RestartAltIcon />
            </IconBtn>
          )}

          {children}
        </Box>
      </Toolbar>
    </Box>
  )
}

export const MUITableToolBar = ({
  hasUnsavedRows,
  isSaving,
  saveChanges,
  discardChanges,
  dataToDownload,
  fileName,
  apiRef = null,
  selectedRows,
  onBulkUpdateButtonClick,
  onDeleteRowsButtonClick,
  isDeleting = false,
  uploadData,
  isUploading = false,
  isLoading = false,
  customDownloadDataMethod = undefined,
}: MUIToolbarComponentProps & {
  fileName: string
}) => {
  if (isLoading) {
    return () => (
      <Toolbar>
        <CircularProgress size={18} />
      </Toolbar>
    )
  }

  return ({ children }: { children?: ReactNode }) => (
    <MUITableToolBarComponent
      hasUnsavedRows={hasUnsavedRows}
      isSaving={isSaving}
      saveChanges={saveChanges}
      discardChanges={discardChanges}
      dataToDownload={dataToDownload}
      fileName={fileName}
      apiRef={apiRef}
      selectedRows={selectedRows}
      onBulkUpdateButtonClick={onBulkUpdateButtonClick}
      onDeleteRowsButtonClick={onDeleteRowsButtonClick}
      isDeleting={isDeleting}
      uploadData={uploadData}
      isUploading={isUploading}
      customDownloadDataMethod={customDownloadDataMethod}
      children={children}
    />
  )
}

type ToolBarDownloadCSVButtonProps = {
  fileName: string
  data: Record<string, AcceptedData>[]
  apiRef?: MutableRefObject<GridApiPremium> | null
  customDownloadDataMethod?: (
    data: Record<string, AcceptedData>[]
  ) => Record<string, string>
  expandDownloadRow?: (
    out: Record<string, AcceptedData>,
    row: Record<string, unknown>
  ) => Record<string, AcceptedData>
  alwaysDownloadFields?: string[]
}
const ToolBarDownloadCSVButton = ({
  fileName,
  data,
  apiRef = null,
  customDownloadDataMethod = undefined,
  expandDownloadRow = undefined,
  alwaysDownloadFields = undefined,
}: ToolBarDownloadCSVButtonProps) => {
  const [isDownloading, setIsDownloading] = useState(false)

  const prepareTableData = () => {
    if (!apiRef) {
      return []
    }
    if (customDownloadDataMethod) {
      return customDownloadDataMethod(data)
    }
    // Step 1: The visible columns, plus any hidden column an upload accepts
    // (`alwaysDownloadFields`), in grid order. Without the second part,
    // hiding a required column (PR Factor 1 on packaging routes is hidden by
    // default) produced a file that could not be uploaded back.
    const always = new Set(alwaysDownloadFields ?? [])
    const shown = new Set(
      apiRef.current.getVisibleColumns().map((col) => col.field)
    )
    const visibleColumns = apiRef.current
      .getAllColumns()
      .filter((col) => shown.has(col.field) || always.has(col.field))
    // Step 2: Get filtered and sorted rows
    const rowIds = gridFilteredSortedRowIdsSelector(apiRef) // Row IDs in sorted order
    const rowModels = apiRef.current.getRowModels() // Map of all rows

    // Step 3: Prepare the data
    const tableData = rowIds.map((id) => {
      const row = rowModels.get(id)
      const rowData: Record<string, AcceptedData> = {}
      if (row) {
        visibleColumns.forEach((col) => {
          if (excludedColumns.has(col.field)) {
            return
          }
          if (excelMayThrowIssueColumns.has(col.field)) {
            rowData[col.field] = `'${row[col.field]}`
            return
          }
          rowData[col.field] = row[col.field]
        })
      }
      return row && expandDownloadRow
        ? expandDownloadRow(rowData, row)
        : rowData
    })

    // Step 4: Prepare headers

    return tableData
  }

  const downloadFile = () => {
    setIsDownloading(true)
    if (apiRef) {
      try {
        const rows = prepareTableData()
        downloadAsCsv(fileName, rows)
      } catch (error) {
        console.error('Error preparing or downloading CSV:', error)
      }
    } else {
      try {
        downloadAsCsv(fileName, data)
      } catch (error) {
        console.error('Error downloading CSV:', error)
      }
    }
    setIsDownloading(false)
  }
  return (
    <IconBtn
      title="Download CSV"
      disabled={isDownloading}
      onClick={downloadFile}
    >
      {isDownloading ? (
        <CircularProgress size={13} sx={{ color: 'inherit' }} />
      ) : (
        <DownloadIcon />
      )}
    </IconBtn>
  )
}
